/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';

// Component imports
import { Collapse, Empty, Modal, Row, Typography, Pagination } from 'antd';

// Misc imports
import { useFetchKnowledgeBaseArticlesQuery } from '../../store/knowledgeBases/knowledgeBasesApi';
import { useFetchBotsQuery } from '../../store/bots/botsApi';
import RichTextEditor from '../Forms/Fields/RichTextEditor/RichTextEditor';

// Constant declarations
const { Paragraph } = Typography;
const PAGE_SIZE = 10;
const KnowledgeBaseArticlesModal = ({
  isModalOpen = false,
  closeModal = () => {},
  selectedKnowledgeBase
}) => {
  const { t } = useTranslation();
  const { orgId, tenantId } = useParams();

  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);

  const { data: knowledgeBaseArticles } = useFetchKnowledgeBaseArticlesQuery(
    { orgId, knowledgeBaseId: selectedKnowledgeBase?.id, urlParams: {
      page: currentPage,
      pageSize: pageSize
    } },
    { skip: !selectedKnowledgeBase }
  );

  const { data: bots } = useFetchBotsQuery({ orgId });
  const primaryBotLanguage = bots?.items.find(bot => bot.id === tenantId)?.settings?.languages?.primary || 'en';

  const handlePageChange = (page, pageSize) => {
    setCurrentPage(page - 1);
    setPageSize(pageSize);
  };

  return (
    <Modal
      title={
        <Paragraph className="d-flex mb-0">
          {selectedKnowledgeBase?.name}
        </Paragraph>
      }
      open={isModalOpen}
      onCancel={closeModal}
      onOk={() => {}}
      centered
      footer={null}
      className="flex-auto overflow-y-auto w-100 px-6 py-5"
    >
      <Collapse defaultActiveKey={[]} accordion={true} expandIconPosition={'right'}>
        {knowledgeBaseArticles?.items?.length === 0 && (
          <Row className="h-100 py-2" align="middle" justify="center">
            <Empty description={t('No articles found.')} />
          </Row>
        )}
        {knowledgeBaseArticles?.items?.map((article) => {
          return (
            <Collapse.Panel header={article.content?.[0]?.title} key={article.id}>
              <RichTextEditor
                style={{ maxHeight: 600 }}
                disabled
                value={`${article.content?.find(tmp => tmp.languageCode === primaryBotLanguage)?.body}`}
                onChange={() => {}}
                isToolbarOpen={false}
              />
              <Paragraph className="d-flex mb-0 mt-5">
                <strong>Topic:</strong>&nbsp;{article.content?.find(tmp => tmp.languageCode === primaryBotLanguage)?.tags?.join(', ')}
              </Paragraph>
            </Collapse.Panel>
          );
        })}
      </Collapse>
      <Pagination
        className="justify-content-end d-flex mt-4"
        current={currentPage + 1}
        onChange={handlePageChange}
        total={knowledgeBaseArticles?.total} // Make sure your API provides the total number of articles
        pageSize={pageSize}
        hideOnSinglePage={true}
      />
    </Modal>
  );
};

KnowledgeBaseArticlesModal.propTypes = {
  isModalOpen: PropTypes.bool,
  closeModal: PropTypes.func,
  selectedKnowledgeBase: PropTypes.string
};

export default KnowledgeBaseArticlesModal;
